import type { CatalogResource } from '../../types.js';
import { FEATURE_GROUP_KEYS } from '../feature-groups.js';
import { ROLE_KEYS } from '../roles.js';

const GROUP = FEATURE_GROUP_KEYS.COMPANY_MANAGEMENT;

/**
 * The `user` resource governs team & access management — who can invite/remove
 * users, and (via the routes guarded by it) who can create permission sets and
 * change what a user is assigned. Company owners get every action. Employees may
 * view, invite and edit their colleagues, but NOT `delete` — removing a team
 * member stays an owner-only action.
 */
export const companyManagement = [
  {
    key: 'user',
    name: 'User',
    description: 'Team members, roles and access',
    group: GROUP,
    actions: ['read', 'create', 'edit', 'delete'],
    grants: {
      [ROLE_KEYS.COMPANY_ADMIN]: 'all',
      [ROLE_KEYS.COMPANY_CREATOR]: ['read', 'create', 'edit'],
      [ROLE_KEYS.COMPANY_READER]: ['read'],
    },
  },
  {
    key: 'constant-mapping',
    name: 'Constant Mapping',
    description:
      'Company material, product-type and facility-process constant mappings',
    group: GROUP,
    actions: ['read', 'create', 'edit', 'delete'],
    grants: {
      [ROLE_KEYS.COMPANY_ADMIN]: 'all',
      [ROLE_KEYS.COMPANY_CREATOR]: ['read'],
      [ROLE_KEYS.COMPANY_READER]: ['read'],
    },
  },
  {
    key: 'api-key',
    name: 'API Key',
    description: "Manage the company's API keys",
    group: GROUP,
    actions: ['read', 'create', 'delete'],
    grants: {
      [ROLE_KEYS.COMPANY_ADMIN]: 'all',
      [ROLE_KEYS.COMPANY_CREATOR]: 'all',
      [ROLE_KEYS.COMPANY_READER]: ['read'],
    },
  },
  {
    // Administering the permission model itself: roles, what each role grants,
    // who belongs to a role, and per-record allow/deny exceptions. Split out of
    // `user` so that letting an employee manage team members no longer also lets
    // them rewrite the tenant's permissions — `user:edit` used to gate both.
    // Owner-only by design; employees may view the model but not change it.
    key: 'permission-access',
    name: 'Permission & Access',
    description: 'Roles, permission sets and per-record access',
    group: GROUP,
    actions: ['read', 'create', 'edit', 'delete'],
    grants: {
      [ROLE_KEYS.COMPANY_ADMIN]: 'all',
      [ROLE_KEYS.COMPANY_CREATOR]: ['read'],
      [ROLE_KEYS.COMPANY_READER]: ['read'],
    },
  },
] as const satisfies readonly CatalogResource[];
